import React, { useState, useEffect } from "react";
import { BookOpen } from "lucide-react";
import Toast, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, getSchoolSettings, getActiveAcademicYear, saveStorage, loadSettings, getModuleCompletion } from "./utils";
import AdminAPI from "../../services/AdminAPI";

function Tag({ color, children }) {
  const colors = {
    green: "bg-[#eaf8f1] text-[#2d9b68] border border-[#b6e8d3]",
    red: "bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0]",
    orange: "bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1]",
    blue: "bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3]",
    gray: "bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3]",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

function Window({ title, note, children }) {
  return (
    <section className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-[#e6ecf3]">
        <div className="font-semibold text-[#233445] text-[15px]">{title}</div>
        {note && <div className="text-xs text-[#617587] mt-0.5">{note}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function PreviewCard({ label, value }) {
  return (
        <div className="bg-[#f5f7fb] rounded-[14px] p-4 mb-3 last:mb-0">
      <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm text-[#233445]">{value}</div>
    </div>
  );
}

const MODULE_KEYS = [
  { key: "identity", label: "Institutional Identity" },
  { key: "schoolInfo", label: "School Information" },
  { key: "teams", label: "Teams & Committees" },
  { key: "market", label: "Market Dynamics" },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "swot", label: "SWOT" },
  { key: "goals", label: "Goals" },
  { key: "actions", label: "Action Plans" },
  { key: "monitoring", label: "Monitoring" },
];


function getModuleSignal(moduleKey) {
  const data = readStorage(STORAGE_KEYS[moduleKey], null);
  if (!data) return "No data yet";
  switch (moduleKey) {
    case "identity": return data.vision ? "Direction mostly defined" : "Vision still unclear";
    case "schoolInfo": return data.schoolName ? "Baseline profile available" : "Profile incomplete";
    case "teams": return data.leadership?.length ? "Ownership structure visible" : "Governance weak";
    case "market": return data.competitors?.length || data.signals?.length ? "External context visible" : "Market context weak";
    case "diagnosis": return data.findings?.length ? "Diagnostic clarity emerging" : "Diagnosis shallow";
    case "swot": return data.points?.length ? "Strategic structure visible" : "SWOT incomplete";
    case "goals": return data.goals?.length ? "Goals identified" : "Goals missing";
    case "actions": return data.actions?.length ? "Execution mapped" : "Execution not mapped";
    case "monitoring": return data.reviews?.length ? "Monitoring started" : "Monitoring not active";
    default: return "No signal";
  }
}

function statusFromPercent(p) {
  if (p >= 80) return { label: "Strong", color: "green" };
  if (p >= 50) return { label: "Partial", color: "orange" };
  if (p > 0) return { label: "Weak", color: "blue" };
  return { label: "Not Started", color: "red" };
}

function buildHighlights() {
  const highlights = [];
  const identity = readStorage(STORAGE_KEYS.identity, {});
  const swot = readStorage(STORAGE_KEYS.swot, { points: [] });
  const goals = readStorage(STORAGE_KEYS.goals, { goals: [] });
  const monitoring = readStorage(STORAGE_KEYS.monitoring, { reviews: [] });

  if (identity.vision && identity.mission) {
    highlights.push("The school has a defined vision and mission, which gives direction to planning.");
  }
  const strengths = (swot.points || []).filter(p => p.quadrant === "Strength");
  if (strengths.length) {
    highlights.push(`The school has identified ${strengths.length} strategic strength(s), showing awareness of existing assets.`);
  }
  if ((goals.goals || []).length) {
    highlights.push(`The school has translated planning into ${goals.goals.length} strategic goal(s).`);
  }
  if ((monitoring.reviews || []).length) {
    highlights.push(`Monitoring has started with ${monitoring.reviews.length} checkpoint log(s), showing some implementation follow-through.`);
  }
  return highlights.length ? highlights : ["More modules need meaningful input before highlights can be generated."];
}

function buildConcerns() {
  const concerns = [];
  const diagnosis = readStorage(STORAGE_KEYS.diagnosis, { findings: [] });
  const swot = readStorage(STORAGE_KEYS.swot, { points: [] });
  const market = readStorage(STORAGE_KEYS.market, { signals: [] });
  const monitoring = readStorage(STORAGE_KEYS.monitoring, { reviews: [] });

  const highFindings = (diagnosis.findings || []).filter(f => f.severity === "High" || f.severity === "Critical");
  if (highFindings.length) concerns.push(`${highFindings.length} high-severity diagnostic issue(s) need attention.`);

  const threats = (swot.points || []).filter(p => p.quadrant === "Threat" && p.priority === "High");
  if (threats.length) concerns.push(`${threats.length} high-priority threat(s) are visible in SWOT.`);

  const highThreatSignals = (market.signals || []).filter(s => s.type === "Threat" && s.impact === "High");
  if (highThreatSignals.length) concerns.push(`${highThreatSignals.length} high-impact external threat signal(s) are affecting the school environment.`);

  const blockers = (monitoring.reviews || []).filter(r => r.blocker && r.blocker.trim() !== "");
  if (blockers.length) concerns.push(`${blockers.length} monitoring log(s) currently report blockers or execution risks.`);

  return concerns.length ? concerns : ["No major concern clusters are visible yet, or more data is needed."];
}

function computeReport() {
  const completions = MODULE_KEYS.map(m => getModuleCompletion(m.key));
  const overall = Math.round(completions.reduce((a, b) => a + b, 0) / completions.length);
  const completed = MODULE_KEYS.filter(m => getModuleCompletion(m.key) >= 60).length;
  const concerns = buildConcerns().filter(c => !c.startsWith("No major"));
  const highlights = buildHighlights();

  const readiness = overall >= 80 ? "High" : overall >= 50 ? "Medium" : "Low";

  const actions = readStorage(STORAGE_KEYS.actions, { actions: [] }).actions || [];
  const monitoring = readStorage(STORAGE_KEYS.monitoring, { reviews: [] }).reviews || [];
  const swot = readStorage(STORAGE_KEYS.swot, { points: [] });
  const goalsData = readStorage(STORAGE_KEYS.goals, { goals: [] });

  let overallCommentary = "The school does not yet have enough data for a meaningful summary.";
  if (overall >= 80) overallCommentary = "The school has built a fairly mature planning structure across identity, diagnosis, strategy, execution, and review.";
  else if (overall >= 50) overallCommentary = "The school has a useful planning base, but some modules still need stronger detail before full strategic confidence is possible.";
  else if (overall > 0) overallCommentary = "The school has started building a planning structure, but it is still too incomplete for strong reporting.";

  let executionCommentary = "Execution planning and monitoring are not yet strong enough to judge implementation maturity.";
  if (actions.length && monitoring.length) executionCommentary = "Execution has moved beyond planning because action items and monitoring checkpoints are both visible.";
  else if (actions.length) executionCommentary = "Execution planning exists, but monitoring depth is still limited.";

  let nextStep = "Complete more modules before relying on this reporting view.";
  if (overall >= 80) nextStep = "Use the complete view for review meetings and begin tightening weak modules through regular monitoring cycles.";
  else if (overall >= 50) nextStep = "Strengthen lower-completion modules and sharpen KPI, ownership, and monitoring depth.";
  else if (!(goalsData.goals || []).length) nextStep = "Translate diagnosis and SWOT into clearer goals before moving too quickly into reporting.";

  const strengths = (swot.points || []).filter(p => p.quadrant === "Strength");
  const printStrength = strengths.length
    ? `${strengths.length} strategic strength(s) are currently visible, including ${strengths.slice(0, 2).map(s => s.title).join(" and ")}.`
    : "No clear strategic strength cluster has been captured yet.";

  const printExecution = actions.length || monitoring.length
    ? `${actions.length} action item(s) and ${monitoring.length} monitoring log(s) are currently present in the system.`
    : "Execution and monitoring depth are still limited.";

  const printOverall = `Overall module completion currently stands at ${overall}%, with ${completed} module(s) showing usable strength.`;
  const printModuleSummary = MODULE_KEYS.map(m => `${m.label}: ${getModuleCompletion(m.key)}%`).join(" | ");
  const printConcerns = concerns.join(" ");

  return {
    overall, completed, readiness, highlights, concerns,
    overallCommentary, executionCommentary, nextStep,
    printStrength, printExecution, printOverall, printModuleSummary, printConcerns, nextStep,
  };
}

export default function ReportsSummary() {
  const [report, setReport] = useState(null);
  const [school, setSchool] = useState(getSchoolSettings);

  useEffect(() => {
    setReport(computeReport());
    loadSettings().then(s => { if (s?.schoolName) setSchool(s); });
    AdminAPI.sdpGetBulk(getActiveAcademicYear()).then(res => {
      if (res?.modules) {
        Object.entries(res.modules).forEach(([key, data]) => {
          if (!data || !STORAGE_KEYS[key]) return;
          if (key === "goals" && data.goals) {
            data = {
              ...data,
              goals: data.goals.map(g => ({ ...g, title: g.title || g.goal || "", goal: g.goal || g.title || "" })),
            };
          }
          saveStorage(STORAGE_KEYS[key], data);
        });
        setReport(computeReport());
      }
    }).catch(() => {});
  }, []);

  function handleRefresh() {
    setReport(computeReport());
    showToast("Report refreshed from latest module data.", "success");
  }

  function handlePrint() {
    if (!report) return;

    const readinessClr = report.readiness === "High" ? "#2d9b68" : report.readiness === "Medium" ? "#f47c20" : "#d95353";
    const readinessBg  = report.readiness === "High" ? "#eaf8f1" : report.readiness === "Medium" ? "#fff4eb" : "#fff0f0";
    const dateStr = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

    const moduleRows = MODULE_KEYS.map(m => {
      const pct = getModuleCompletion(m.key);
      const status = pct >= 80 ? "Strong" : pct >= 50 ? "Partial" : pct > 0 ? "Weak" : "Not Started";
      const clr = pct >= 80 ? "#2d9b68" : pct >= 50 ? "#f47c20" : pct > 0 ? "#2d81c1" : "#8ea0b1";
      return `<tr style="page-break-inside:avoid;">
        <td style="padding:7px 10px;border-bottom:1px solid #f0f4f8;font-size:9pt;color:#233445;">${m.label}</td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f4f8;">
          <div style="display:flex;align-items:center;gap:8px;">
            <div style="flex:1;height:5px;background:#e6ecf3;border-radius:4px;overflow:hidden;min-width:80px;">
              <div style="height:5px;background:${clr};border-radius:4px;width:${pct}%;"></div>
            </div>
            <span style="font-weight:700;color:${clr};font-size:9pt;min-width:34px;text-align:right;">${pct}%</span>
          </div>
        </td>
        <td style="padding:7px 10px;border-bottom:1px solid #f0f4f8;font-weight:600;color:${clr};font-size:9pt;">${status}</td>
      </tr>`;
    }).join("");

    const highlightItems = report.highlights.map(h => `<li style="margin-bottom:5px;line-height:1.5;">${h}</li>`).join("");
    const concernItems = report.concerns.length
      ? report.concerns.map(c => `<li style="margin-bottom:5px;line-height:1.5;">${c}</li>`).join("")
      : `<li style="color:#8ea0b1;">No major concern clusters detected.</li>`;

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>SDP Report — ${school.schoolName}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.7;
    color: #233445;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  @page { size: A4 portrait; margin: 15mm 16mm 15mm 16mm; }

  /* ── Header ── */
  .header { border-bottom: 3px solid #2d81c1; padding-bottom: 14px; margin-bottom: 16px; page-break-inside: avoid; }
  .header-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .header-brand { font-size: 7.5pt; font-weight: 700; color: #2d81c1; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 4px; line-height: 1.4; }
  .header-school { font-size: 18pt; font-weight: 800; color: #1a2e44; line-height: 1.2; }
  .header-sub { font-size: 8.5pt; color: #617587; margin-top: 3px; line-height: 1.5; }
  .readiness-badge { background: ${readinessBg}; border: 1px solid ${readinessClr}66; border-radius: 18px; padding: 5px 14px; font-size: 8.5pt; font-weight: 700; color: ${readinessClr}; white-space: nowrap; margin-top: 4px; }
  .header-meta { display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap; }
  .meta-pill { background: #f5f7fb; border: 1px solid #e6ecf3; border-radius: 14px; padding: 4px 12px; font-size: 8pt; color: #617587; line-height: 1.5; }

  /* ── Stats row ── */
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 9px; margin-bottom: 12px; page-break-inside: avoid; }
  .stat { background: #f5f7fb; border: 1px solid #e6ecf3; border-radius: 8px; padding: 12px 8px; text-align: center; }
  .stat-lbl { font-size: 6.5pt; color: #8ea0b1; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; line-height: 1.5; }
  .stat-val { font-size: 17pt; font-weight: 800; color: #2d81c1; line-height: 1.1; }
  .stat-sub { font-size: 6.5pt; color: #8ea0b1; margin-top: 4px; line-height: 1.5; }

  /* ── Progress bar ── */
  .pb-wrap { background: #f5f7fb; border: 1px solid #e6ecf3; border-radius: 8px; padding: 11px 14px; margin-bottom: 14px; page-break-inside: avoid; }
  .pb-hdr { display: flex; justify-content: space-between; font-size: 8.5pt; font-weight: 600; margin-bottom: 7px; color: #233445; line-height: 1.5; }
  .pb-track { height: 8px; background: #e6ecf3; border-radius: 5px; overflow: hidden; }
  .pb-fill { height: 8px; background: #2d81c1; border-radius: 5px; }

  /* ── Section headings ── */
  .sec { font-size: 11pt; font-weight: 700; color: #1a2e44; border-bottom: 1.5px solid #e6ecf3; padding-bottom: 5px; margin: 18px 0 10px; page-break-after: avoid; }

  /* ── Module table ── */
  table { width: 100%; border-collapse: collapse; font-size: 9pt; page-break-inside: auto; }
  thead { display: table-header-group; }
  thead tr { background: #f5f7fb; }
  th { text-align: left; padding: 8px 11px; font-size: 7.5pt; color: #617587; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; border-bottom: 1.5px solid #e6ecf3; line-height: 1.5; }
  td { padding: 9px 11px; border-bottom: 1px solid #f0f4f8; vertical-align: middle; line-height: 1.6; }
  tr:last-child td { border-bottom: none; }
  tr { page-break-inside: avoid; }

  /* ── Two-column card grid ── */
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; margin: 10px 0; page-break-inside: avoid; }
  .card { background: #f5f7fb; border: 1px solid #e6ecf3; border-radius: 9px; padding: 13px 15px; page-break-inside: avoid; }
  .card-lbl { font-size: 7pt; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #8ea0b1; margin-bottom: 7px; line-height: 1.5; }
  .card-body { font-size: 9pt; color: #233445; line-height: 1.75; }
  .card.hi { background: #eaf8f1; border-color: #b6e8d3; }
  .card.hi .card-lbl { color: #2d9b68; }
  .card.con { background: #fff0f0; border-color: #f5c0c0; }
  .card.con .card-lbl { color: #d95353; }
  .card.nxt { background: #eef6fc; border-color: #cfe3f3; margin-top: 10px; }
  .card.nxt .card-lbl { color: #2d81c1; }
  ul { padding-left: 16px; }
  li { font-size: 9pt; color: #233445; line-height: 1.75; margin-bottom: 3px; }

  /* ── Footer — no page-break-inside:avoid so it stays on the last page ── */
  .footer { margin-top: 20px; padding-top: 9px; border-top: 1px solid #e6ecf3; display: flex; justify-content: space-between; font-size: 7.5pt; color: #8ea0b1; line-height: 1.5; }
</style>
</head>
<body>

<!-- Header (no forced page break) -->
<div class="header">
  <div class="header-top">
    <div>
      <div class="header-brand">Holistic Evaluator · School Development Plan</div>
      <div class="header-school">${school.schoolName}</div>
      <div class="header-sub">Annual School Development Summary Report</div>
    </div>
    <div class="readiness-badge">Readiness: ${report.readiness}</div>
  </div>
  <div class="header-meta">
    <span class="meta-pill">Academic Year: ${school.academicYear}</span>
    <span class="meta-pill">Generated: ${dateStr}</span>
    <span class="meta-pill">Overall: ${report.overall}%</span>
    <span class="meta-pill">${report.completed} / ${MODULE_KEYS.length} modules strong</span>
  </div>
</div>

<!-- Stats strip -->
<div class="stats">
  <div class="stat"><div class="stat-lbl">Overall Completion</div><div class="stat-val">${report.overall}%</div><div class="stat-sub">across all modules</div></div>
  <div class="stat"><div class="stat-lbl">Modules Completed</div><div class="stat-val">${report.completed}</div><div class="stat-sub">meaningful content</div></div>
  <div class="stat"><div class="stat-lbl">Concern Areas</div><div class="stat-val" style="color:#d95353;">${report.concerns.length}</div><div class="stat-sub">risk signals</div></div>
  <div class="stat"><div class="stat-lbl">Institution Readiness</div><div class="stat-val" style="font-size:12pt;color:${readinessClr};">${report.readiness}</div><div class="stat-sub">planning maturity</div></div>
</div>

<!-- Progress bar -->
<div class="pb-wrap">
  <div class="pb-hdr"><span>Overall Planning Progress</span><span style="color:#f47c20;">${report.overall}%</span></div>
  <div class="pb-track"><div class="pb-fill" style="width:${report.overall}%;"></div></div>
</div>

<!-- Module table -->
<div class="sec">Module Completion Overview</div>
<table>
  <thead><tr><th style="width:36%;">Module</th><th style="width:44%;">Completion</th><th style="width:20%;">Status</th></tr></thead>
  <tbody>${moduleRows}</tbody>
</table>

<!-- Highlights & Concerns -->
<div class="sec">Strategic Highlights &amp; Concerns</div>
<div class="two-col">
  <div class="card hi"><div class="card-lbl">Key Highlights</div><ul>${highlightItems}</ul></div>
  <div class="card con"><div class="card-lbl">Concern Areas</div><ul>${concernItems}</ul></div>
</div>

<!-- Executive Commentary -->
<div class="sec">Executive Commentary</div>
<div class="two-col">
  <div class="card"><div class="card-lbl">Overall View</div><div class="card-body">${report.overallCommentary}</div></div>
  <div class="card"><div class="card-lbl">Execution &amp; Monitoring</div><div class="card-body">${report.executionCommentary}</div></div>
</div>
<div class="card nxt"><div class="card-lbl">Recommended Next Step</div><div class="card-body">${report.nextStep}</div></div>

<!-- Footer -->
<div class="footer">
  <span>${school.schoolName} · School Development Plan · ${school.academicYear}</span>
  <span>Generated via Holistic Evaluator · ${dateStr}</span>
</div>

</body>
</html>`;

    const win = window.open("", "_blank", "width=860,height=720");
    if (!win) { showToast("Allow pop-ups to print the report.", "error"); return; }
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  }

  if (!report) return null;

  const readinessColor = report.readiness === "High" ? "green" : report.readiness === "Medium" ? "orange" : "red";

  return (
    <>
      <div className="flex-1 flex flex-col min-w-0 border-l-2 border-slate-200 ml-1">
        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full font-[poppins]">
          <section className="mb-8 print:hidden">
            <div className="text-center px-4 py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">Reports & Summary</h1>
              <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                Review overall planning maturity, concern clusters, and executive insights across all SDP modules.
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 print:hidden">
            {[
              { label: "Overall Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${report.overall}%`, meta: "combined across all modules" },
              { label: "Modules Completed", icon: "✅", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: report.completed, meta: "modules with meaningful content" },
              { label: "Concern Areas", icon: "⛔", iconCls: "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]", value: report.concerns.filter(c => !c.startsWith("No major")).length, meta: "risk or blocker signals" },
              { label: "Institution Readiness", icon: "🧭", iconCls: readinessColor === "green" ? "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]" : readinessColor === "orange" ? "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]" : "bg-[#fff0f0] text-[#d95353] border-[#f4c5c5]", value: report.readiness, meta: "planning maturity" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                <div className="flex items-center justify-between gap-[10px] mb-4">
                  <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                  <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>
                    {s.icon}
                  </div>
                </div>
                <div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                </div>
              </div>
            ))}
          </section>

          {/* Progress */}
          <div className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm p-5 mb-6 print:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#233445]">Overall Planning Progress</span>
              <span className="text-sm font-bold text-[#f47c20]">{report.overall}%</span>
            </div>
            <div className="h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
              <div className="h-full bg-[#f47c20] rounded-full transition-all duration-500" style={{ width: `${report.overall}%` }} />
            </div>
          </div>

          {/* Module Summary + Highlights */}
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5 mb-5 print:hidden">
            <Window title="Module Completion Summary" note="Snapshot of how far each portal section has been filled meaningfully.">
              <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]">
                      {["Module", "Completion", "Status", "Summary Signal"].map(h => (
                        <th key={h} className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {MODULE_KEYS.map(m => {
                      const pct = getModuleCompletion(m.key);
                      const st = statusFromPercent(pct);
                      return (
                        <tr key={m.key} className="border-b border-[#e6ecf3] last:border-0 hover:bg-[#fbfcfe] transition-colors">
                          <td className="px-4 py-3 font-medium text-[#233445]">{m.label}</td>
                          <td className="px-4 py-3 text-[#617587]">{pct}%</td>
                          <td className="px-4 py-3"><Tag color={st.color}>{st.label}</Tag></td>
                          <td className="px-4 py-3 text-[#617587]">{getModuleSignal(m.key)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Window>

            <Window title="Report Highlights" note="What stands out most from the current portal data.">
              {report.highlights.map((h, i) => (
                <div key={i} className="bg-[#eaf8f1] rounded-[14px] border border-[#b6e8d3] p-4 mb-3 last:mb-0">
                  <div className="text-sm font-semibold text-[#2d9b68] mb-1">Highlight</div>
                  <div className="text-sm text-[#617587]">{h}</div>
                </div>
              ))}
            </Window>
          </div>

          {/* Concerns + Commentary */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mb-5 print:hidden">
            <Window title="Concern Areas" note="Weaknesses, blockers, or external threats that deserve attention.">
              {report.concerns.map((c, i) => (
                <div key={i} className="bg-[#fff0f0] rounded-[14px] border border-[#f5c0c0] p-4 mb-3 last:mb-0">
                  <div className="text-sm font-semibold text-[#d95353] mb-1">Concern</div>
                  <div className="text-sm text-[#617587]">{c}</div>
                </div>
              ))}
            </Window>

            <Window title="Readiness Commentary" note="Interpretive summary based on what has been entered.">
              <PreviewCard label="Overall View" value={report.overallCommentary} />
              <PreviewCard label="Execution View" value={report.executionCommentary} />
              <PreviewCard label="Next Best Step" value={report.nextStep} />
            </Window>
          </div>

          {/* Printable Executive Summary */}
          <section className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm overflow-hidden" id="printArea">
            <div className="px-6 py-4 border-b border-[#e6ecf3] print:hidden">
              <div className="font-semibold text-[#233445] text-[15px]">Printable Executive Summary</div>
              <div className="text-xs text-[#617587] mt-0.5">A formatted review block that can be printed or shared internally.</div>
            </div>
            <div className="p-6">
              {/* Print Header */}
              <div className="flex items-start justify-between gap-4 mb-6 pb-4 border-b border-[#e6ecf3]">
                <div>
                  <div className="text-xl font-bold text-[#233445] mb-0.5">{school.schoolName}</div>
                  <div className="text-sm text-[#617587]">School Development Summary Report • Academic Year {school.academicYear}</div>
                </div>
                <Tag color={readinessColor}>Readiness: {report.readiness}</Tag>
              </div>

              {/* Print Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { title: "Overall Completion", body: report.printOverall },
                  { title: "Most Visible Strengths", body: report.printStrength },
                  { title: "Top Concern Areas", body: report.printConcerns || "No major concerns detected yet." },
                  { title: "Execution & Monitoring View", body: report.printExecution },
                ].map(box => (
                  <div key={box.title} className="bg-[#f5f7fb] rounded-[14px] p-4 border border-[#e6ecf3]">
                    <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-2">{box.title}</div>
                    <div className="text-sm text-[#233445]">{box.body}</div>
                  </div>
                ))}
              </div>

              <div className="bg-[#f5f7fb] rounded-[14px] p-4 border border-[#e6ecf3] mb-4">
                <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-2">Recommended Next Step</div>
                <div className="text-sm text-[#233445]">{report.nextStep}</div>
              </div>

              <div className="bg-[#f5f7fb] rounded-[14px] p-4 border border-[#e6ecf3]">
                <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-2">Module Snapshot</div>
                <div className="text-sm text-[#233445] leading-relaxed">{report.printModuleSummary}</div>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mt-8 mb-8 print:hidden">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#1D3469]">Report Actions</h3>
                <p className="text-sm text-gray-500">Refresh report data and print executive summary.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button onClick={handleRefresh} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                  Refresh Report
                </button>
                <button onClick={handlePrint} className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-6 py-3 rounded-lg font-medium transition-colors">
                  Print Summary
                </button>
              </div>
            </div>
          </section>
          </main>
        </div>
      </div>
      <Toast />
    </>
  );
}
